/**
 * Reconciliation Run Table Component
 * Feature: 029-reconciliation-services (T058)
 * Constitution Version: 1.11.0
 *
 * Displays paginated run history with filters.
 * Spec 197 — migrated off MUI onto the token-driven kit.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DataTable, Select, Badge, AppTooltip, Spinner, Alert } from '../ui';
import type { DataTableColumn, BadgeVariant } from '../ui';
import { reconciliationApi } from '../../services/reconciliationApi';
import type { ReconciliationRunHistory, RunStatus } from '../../types/reconciliation.types';
import { formatEastern } from '@/utils/dateFormatter';

const STATUS_COLORS: Record<RunStatus, BadgeVariant> = {
  completed: 'success',
  failed: 'error',
  running: 'info',
};

export default function ReconciliationRunTable() {
  const [serviceName, setServiceName] = useState<string>('');
  const [status, setStatus] = useState<string>('');

  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['reconciliation', 'runs', serviceName, status],
    queryFn: () =>
      reconciliationApi.getRuns({
        service_name: serviceName || undefined,
        status: status as any,
        limit: 50,
      }),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const handleViewRun = (runId: string) => {
    navigate(`/reconciliation/runs/${runId}`);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">Failed to load runs: {(error as Error).message}</Alert>;
  }

  const runs = data?.runs || [];

  const columns: DataTableColumn<ReconciliationRunHistory>[] = [
    { key: 'service_name', header: 'Service' },
    {
      key: 'status',
      header: 'Status',
      render: (run) => <Badge variant={STATUS_COLORS[run.status]}>{run.status}</Badge>,
    },
    {
      key: 'started_at',
      header: 'Started',
      render: (run) => formatEastern(run.started_at),
    },
    {
      key: 'completed_at',
      header: 'Duration',
      // Spec 292 T040: derived from the payload's started_at/completed_at (no duration_seconds is sent); open while null
      render: (run) => {
        const secs = run.completed_at ? Math.round((new Date(run.completed_at).getTime() - new Date(run.started_at).getTime()) / 1000) : null;
        return secs != null ? `${secs}s` : 'Running…';
      },
    },
    { key: 'total_items_checked', header: 'Items Checked', align: 'right' },
    { key: 'drifts_detected', header: 'Drifts', align: 'right' },
    { key: 'auto_fixes_applied', header: 'Auto-fixes', align: 'right' },
    { key: 'failures', header: 'Failures', align: 'right' },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: (run) => (
        <AppTooltip title="View Details">
          <button
            onClick={() => handleViewRun(run.id)}
            aria-label="View Details"
            className="inline-flex items-center justify-center rounded-[var(--radius-md)] p-1.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]"
          >
            <Eye className="h-4 w-4" />
          </button>
        </AppTooltip>
      ),
    },
  ];

  return (
    <div>
      {/* Filters */}
      <div className="mb-4 flex gap-3">
        <Select
          label="Service Name"
          selectSize="sm"
          value={serviceName}
          onChange={(e) => setServiceName(e.target.value)}
          options={[
            { value: '', label: 'All Services' },
            { value: 'AgentDeployment', label: 'Agent Deployment' },
            { value: 'WorkspaceMetadata', label: 'Workspace Metadata' },
            { value: 'ServicePrincipal', label: 'Service Principal' },
          ]}
        />
        <Select
          label="Status"
          selectSize="sm"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={[
            { value: '', label: 'All Statuses' },
            { value: 'completed', label: 'Completed' },
            { value: 'failed', label: 'Failed' },
            { value: 'in_progress', label: 'In Progress' },
          ]}
        />
      </div>

      <DataTable
        columns={columns}
        rows={runs}
        rowKey={(run) => run.id}
        pageSize={20}
        emptyMessage="No reconciliation runs found."
      />
    </div>
  );
}
